import { Injectable } from '@angular/core';
import { filterNil } from '@datorama/akita';
import { TranslateService } from '@ngx-translate/core';
import uniqBy from 'lodash-es/uniqBy';
import { Observable, of } from 'rxjs';
import { catchError, first, map, tap } from 'rxjs/operators';

import { IDocument } from 'document/document';
import { DocumentOutput } from 'document/model/document.output';
import { DocumentConverterPort } from 'document/port/document-converter.port';
import { FetchDocumentsQuery } from 'document/query/fetch-documents.query';
import { UserApplicationPort } from 'user/port/user-application.port';

import { AppLocator } from '../../app.locator';
import { SaveDocumentCmd } from './command/save-document.cmd';
import { DocumentApiPort } from './port/document-api.port';
import { DocumentApplicationPort } from './port/document-application.port';
import { DocumentRepositoryPort } from './port/document-repository.port';

@Injectable()
export class DocumentApplicationAdapter implements DocumentApplicationPort {
	constructor(
		private _documentApiPort: DocumentApiPort,
		private _documentRepositoryPort: DocumentRepositoryPort,
		private _documentConverterPort: DocumentConverterPort
	) {}

	public saveDocument(cmd: SaveDocumentCmd): Promise<DocumentOutput> {
		return this._documentApiPort
			.saveDocument(cmd)
			.pipe(tap((document) => this.saveDocuments([document])))
			.toPromise();
	}

	public observeDocuments(ids: string[]): Observable<IDocument[]> {
		return this._documentRepositoryPort.observeDocuments(ids);
	}

	public fetchDocuments(
		query: FetchDocumentsQuery
	): Observable<string[] | Error> {
		return this._documentApiPort.fetchDocuments(query).pipe(
			tap((documents) => this.saveDocuments(documents)),
			map((documents) => documents.map((document) => document.id)),
			catchError((e) => of(Error(e)))
		);
	}

	public saveDocuments(documents: DocumentOutput[]): void {
		const entities = uniqBy(documents, 'id').map((document) =>
			this._documentConverterPort.documentAsEntity(document)
		);

		this._documentRepositoryPort.saveDocuments(entities);
	}
}
